import { Location } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ArrayFunctions, InputDefault, ObjectFunctions } from '@campus/utils';
import '@diekeure/dcr-components/dcr-switch.js';
import { BehaviorSubject, defer, fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { InputBooleanAttribute, isTruthyInput } from '../core/empty-attribute';
import { SelectableListController } from '../core/selection/selectable-list.controller';
import { SelectionModel } from '../core/selection/selection.model';
import { MenuSelectionChange } from '../menu';
import { RowInterface, RowSelectionChange, TableRowComponent } from './row/row.component';
import {
  GroupedData,
  TableBulkActionInterface,
  TableCellSwitchChange,
  TableColumnConfigInterface,
  TableFilterInterface,
  TableGroupActionInterface,
  TablePaginationInterface,
  TableRowActionInterface,
  TableRowParentInterface,
  TableSelectionChange,
  TableShowOnlySelectionChange,
  TableSortInterface,
  TableStateInterface,
  TableVisibleColumnsChange,
} from './table.interface';
import { TABLE_TOOLBAR_ACTION, TableToolbarActionInterface } from './toolbar';

const SCROLL_Y_THRESHOLD = 48;
const SCROLL_X_THRESHOLD = 12;

/**
 * You'll probably want to add either the Table2LocalHandlingDirective or the Table2ApiV2Directive to this component.
 * This component only handles presentation, interaction and selection of the table rows, not data management.
 */
@Component({
  standalone: false,
  selector: 'campus-table-v2',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T = any>
  implements TableRowParentInterface, OnChanges, OnInit, AfterContentInit, AfterViewInit, OnDestroy {
  @ViewChildren('rowRef', { read: TableRowComponent }) rows: QueryList<RowInterface<T>>;
  @ViewChild('scrollContainer', { read: ElementRef, static: false }) scrollContainer: ElementRef;

  @HostBinding('class')
  defaultClasses = [
    'cds-table-v2',
    'relative',
    'flex',
    'flex-column',
    'min-block-size-full',
    'border',
    'corner-m',
    'overflow-hidden',
  ];

  //#region Inputs
  @Input() @InputDefault(false) loading: boolean;

  @Input() @InputDefault(() => []) data: T[];
  @Input() @InputDefault('id' as any) idKey: Extract<keyof T, number>;
  @Input() @InputDefault(() => []) columns: TableColumnConfigInterface<T>[];
  @Input() @InputDefault(() => []) filters: TableFilterInterface<T>[];
  @Input()
  @InputDefault(() => ({ key: undefined, direction: undefined }))
  sortBy: TableSortInterface<T>;
  @Input() groupBy: string;

  @Input() @InputDefault('nl-BE') locale: string;
  @Input() headline: string;

  @HostBinding('attr.aria-multiselectable')
  @Input()
  multiple = false;

  @Input() @InputDefault(true) showCheckAll: boolean;
  @Input() @InputDefault(true) showOnlySelectedButton: boolean;
  @Input() selectable: InputBooleanAttribute = false;
  /**
   * Optional per-row predicate. When provided and it returns `false` for a row, that row cannot be
   * selected and renders the checkbox in a disabled state — the row stays visible and column-aligned. When omitted, every
   * row is selectable as before.
   */
  @Input() isRowSelectable?: (row: T) => boolean;
  @Input() disableGrowSearchOnFocus: InputBooleanAttribute;
  @Input() disableTooltipOnTruncated: InputBooleanAttribute;
  @Input() disableTooltip = false;
  @Input() showRefresh: InputBooleanAttribute = false;
  @Input() @InputDefault(true) showSearchTerm: boolean;
  @Input() @InputDefault(false) showFiltersOnInit: boolean;

  @Input() @InputDefault(10) pageSize: number;
  @Input() pagination: TablePaginationInterface;
  @Input() @InputDefault(() => [10, 25, 50, 100]) pageSizeOptions: number[];

  @Input() compareWith: (o1: any, o2: any) => boolean;

  @Input() readonly = false;

  @Input() rowActions: TableRowActionInterface<T>[];
  @Input() groupActions: TableGroupActionInterface<T>[];
  @Input() bulkActions: TableBulkActionInterface<T>[];

  @Input() @InputDefault(() => []) initialSelection: T[];

  @Input() @InputDefault(null) searchTerm: string | null;
  @Input() @InputDefault(false) searchTermOnEnter: boolean;

  @Input() @InputDefault('Geen resultaten gevonden') noResultsMessage: string;
  @Input() @InputDefault('Geen data gevonden') noDataMessage: string;

  //#endregion

  //#region Public Properties
  @HostBinding('attr.data-has-pagination')
  public hasPagination: boolean | undefined;

  @ContentChild('inlineActions') inlineActionsTemplate?: TemplateRef<any>;

  /**
   * The rows the table is rendering: what is left of the data after the search term, the column filters and
   * the current page. Written by whichever handling directive sits on the table; setting it announces the
   * rows through {@link visibleRowsChange}.
   */
  public get dataSource(): T[] | GroupedData<T>[] {
    return this._dataSource;
  }

  public set dataSource(dataSource: T[] | GroupedData<T>[]) {
    this._dataSource = dataSource;
    this.visibleRowsChange.emit(this._getVisibleRows());
  }

  public visibleColumns: SelectionModel<TableColumnConfigInterface<T>> = new SelectionModel<
    TableColumnConfigInterface<T>
  >(true);
  public filterableColumns: TableColumnConfigInterface<T>[] = [];
  public selectableColumns: TableColumnConfigInterface<T>[] = [];

  public showFilters = false;
  public filtersForm: FormArray;
  public searchFocus: boolean;
  public columnConfigDict: Record<string, TableColumnConfigInterface<T>> = {};

  public page = 1;
  public totalItems = 0;

  public someVisibleRowsChecked: boolean;
  public allVisibleRowsChecked: boolean;
  public showOnlySelection = false;
  public selectableController: SelectableListController<RowInterface<T>>;

  public hasRowActions = false;
  public hasGroupActions = false;

  /** The controls the capabilities on this table contribute — see {@link TABLE_TOOLBAR_ACTION}. */
  public toolbarActions: TableToolbarActionInterface[] = [];

  public scrolled$: Observable<{ x: boolean; y: boolean }>;
  private _scrolled$ = new BehaviorSubject({ x: false, y: false });

  private searchTermsDebounceTime = 300;
  private searchTerms$: Observable<string>;
  private _searchTerms$ = new Subject<string>();
  private searchTermSubscription: Subscription = new Subscription();
  public searchTermControl = new FormControl('');

  public trackById: (index: number, item: T) => number;

  //#endregion

  //#region Private Properties
  private _ngZone = inject(NgZone);
  private _injector = inject(Injector);
  private _formBuilder = inject(FormBuilder);
  private _cd = inject(ChangeDetectorRef);
  private _location = inject(Location);
  private _destroyed$ = new Subject<void>();

  private _dataSource: T[] | GroupedData<T>[] = [];

  /**
   * Carries a silent filter rebuild into the same stream the form's own edits run through, so the two are
   * compared against each other — see `_watchFilterChanges`.
   */
  private _silentFilterWrite$ = new Subject<TableFilterInterface<T>[]>();

  private readonly rowsSelectionChange: Observable<RowSelectionChange<T>> = defer(() => {
    const rows = this.rows;

    if (rows) {
      return rows.changes.pipe(
        startWith(rows),
        switchMap(() => merge(...rows.map((row) => row.selectionChange)))
      );
    }

    return this._ngZone.onStable.pipe(
      take(1),
      switchMap(() => this.rowsSelectionChange)
    );
  }) as Observable<RowSelectionChange<T>>;

  //#endregion

  //#region Outputs
  @Output() columnsChange = new EventEmitter<TableColumnConfigInterface<T>[]>();
  @Output() visibleColumnsChange = new EventEmitter<TableVisibleColumnsChange<T>>();
  @Output() paginationChange = new EventEmitter<TablePaginationInterface>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() filtersChange = new EventEmitter<TableFilterInterface<T>[]>();
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() sortChange = new EventEmitter<TableSortInterface<T>>();
  @Output() selectionChange = new EventEmitter<TableSelectionChange>();
  @Output() refresh = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<GroupedData<T>[] | T[]>();
  @Output() loadingChange = new EventEmitter<boolean>();
  /**
   * The rows on screen, every time they change: the data as narrowed by the search term, the column filters
   * and the current page. Group wrappers are unwrapped, so a consumer sees rows whether the table groups or
   * not. Unlike {@link dataChange}, which carries the data that went in.
   */
  @Output() visibleRowsChange = new EventEmitter<T[]>();

  @Output() showOnlySelectionChange = new EventEmitter<TableShowOnlySelectionChange>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() cellSwitchChange = new EventEmitter<TableCellSwitchChange<T>>();

  //#endregion

  protected filterOptionCompareWith = (o1: any, o2: any) => {
    return this._filterOptionId(o1) === this._filterOptionId(o2);
  };

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    // @InputDefault wraps this method: `undefined`-bound inputs are coalesced to their defaults on
    // both the property and changes[key].currentValue before this body runs.
    let shouldUpdatePagination = false;

    if (changes.data) {
      shouldUpdatePagination = true;
      this.dataChange.emit(this.data);
    }

    if (changes.columns && changes.columns.currentValue) {
      this._setColumnConfigDict();
      this._setFilterableColumns();
      this._setSelectableColumns();
      if (changes.columns.firstChange || !changes.columns.previousValue) {
        this._initVisibleColumnsSelection();
      } else {
        this._setVisibleColumns();
      }
      this.columnsChange.emit(this.columns);
    }

    if (changes.loading) {
      this.loadingChange.emit(this.loading);
    }

    if (changes.filters && changes.filters.currentValue) {
      this._initFilterForm(this.filters);
      shouldUpdatePagination = true;
    }

    if (changes.pageSize || changes.pagination) {
      this.hasPagination = !!this.pageSize || !!this.pagination || undefined;
      this.totalItems = this.pagination?.total ?? 0;
      shouldUpdatePagination = true;
    }

    if (changes.pageSize) {
      if (!changes.pageSize.firstChange) {
        this.page = this._getNewPageFromPageSizeChange(
          this.page,
          changes.pageSize.previousValue,
          changes.pageSize.currentValue
        );
      }
      shouldUpdatePagination = true;
    }

    if (changes.multiple) {
      this.multiple = isTruthyInput(this.multiple);
    }

    if (changes.selectable || changes.multiple) {
      this.selectable = isTruthyInput(this.selectable) || this.multiple;
    }

    if (changes.readonly) {
      this.readonly = isTruthyInput(this.readonly);
    }

    if (changes.rowActions) {
      this.hasRowActions = !!this.rowActions?.length;
    }
    if (changes.groupActions) {
      this.hasGroupActions = !!this.groupActions?.length;
    }
    if (changes.disableGrowSearchOnFocus) {
      this.disableGrowSearchOnFocus = isTruthyInput(this.disableGrowSearchOnFocus);
    }

    if (changes.disableTooltipOnTruncated) {
      this.disableTooltipOnTruncated = isTruthyInput(this.disableTooltipOnTruncated);
    }

    if (changes.disableTooltip) {
      this.disableTooltip = isTruthyInput(this.disableTooltip);
    }

    if (changes.idKey) {
      this._setTrackById(this.idKey);
    }

    if (shouldUpdatePagination) {
      this.pagination = this._getPagination(this.page, this.totalItems);
    }

    if (changes.showFiltersOnInit && changes.showFiltersOnInit.firstChange) {
      this.showFilters = this.showFiltersOnInit;
    }
  }

  ngOnInit() {
    this.setupSearchTerms(false);

    if (!this.filtersForm) {
      this._initFilterForm();
    }

    this.scrolled$ = this._scrolled$.pipe(takeUntil(this._destroyed$));

    this._setTrackById(this.idKey);
  }

  ngAfterContentInit(): void {
    this._initToolbarActions();
    this._initSelectableController();
    this.rowsSelectionChange.pipe(takeUntil(this._destroyed$)).subscribe((event) => {
      this._onSelect(event.source, event.isUserInput);
    });
  }

  ngAfterViewInit(): void {
    this.rows.changes.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      if (!this.selectableController) return;

      this.rows.forEach((row) => {
        const isSelected = this.selectableController.isSelected(row);

        // Both directions, and each guarded by what the row already shows. A bare `isSelected !==
        // row.selected` selects on a mismatch either way, so a row rendered as selected while the model
        // says it is not would be re-selected instead of cleared — and `row.select()` feeds back through
        // `_onSelect` into the model, so the selection could only ever grow.
        if (isSelected && !row.selected) {
          row.select();
        } else if (!isSelected && row.selected) {
          row.deselect();
        }
      });

      const selectableRows = this.rows.toArray().filter((r) => !r.disabled);
      this.allVisibleRowsChecked = selectableRows.every((r) => r.selected);
      this.someVisibleRowsChecked = selectableRows.some((r) => r.selected) && !this.allVisibleRowsChecked;

      this._cd.detectChanges();
    });

    // Trigger initial check, fixes the case where rows are already present on init
    // and thus the changes observable doesn't emit
    this.rows.notifyOnChanges();

    fromEvent(this.scrollContainer?.nativeElement, 'scroll')
      .pipe(
        takeUntil(this._destroyed$),
        map((event: Event) => {
          const { scrollLeft, scrollTop } = event.target as HTMLElement;
          return { y: scrollTop > SCROLL_Y_THRESHOLD, x: scrollLeft > SCROLL_X_THRESHOLD };
        })
      )
      .subscribe((scrolled) => this._scrolled$.next(scrolled));
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.searchTermSubscription.unsubscribe();
  }
  //#endregion

  //#region Public Methods
  onVisibleColumnsChange(event: MenuSelectionChange<TableColumnConfigInterface<T>>): void {
    const added = event.added.map((column) => column.value);
    const removed = event.removed.map((column) => column.value);

    // Kept in step with the selection for the same reason as in `setVisibleColumns`: a later `columns`
    // input would otherwise rebuild the selection from a flag that still describes the previous choice.
    added.forEach((column) => (column.visible = true));
    removed.forEach((column) => (column.visible = false));

    // Rebuilt in config order rather than appended: a column that is toggled back on returns to its own
    // place, the same spot a fresh mount would put it in. A rebuild is needed because `setSelection` only
    // diffs — columns that stay selected would keep their old position.
    this._initVisibleColumnsSelection();

    const changeEvent = new TableVisibleColumnsChange<T>(this.visibleColumns.selected, added, removed);
    this.visibleColumnsChange.emit(changeEvent);
  }

  onSearchTermEnterKeyUp(event: KeyboardEvent): void {
    if (!this.searchTermOnEnter) return;

    this._searchTerms$.next(this.searchTermControl.value);
  }

  onSearchTermFocusOut(event: FocusEvent): void {
    this.searchFocus = false;
    if (!this.searchTermOnEnter) return;

    this._searchTerms$.next(this.searchTermControl.value);
  }

  onSearchTermFocusIn(): void {
    this.searchFocus = true;
  }

  onSearchTermClear(): void {
    if (!this.searchTermOnEnter) return;

    this._searchTerms$.next(this.searchTermControl.value);
  }

  onAddFilter(): void {
    const filter = this._formBuilder.group({
      key: this._formBuilder.control(undefined, { updateOn: 'change' }), // FormControl for key
      value: this._formBuilder.control(undefined, { updateOn: 'blur' }), // FormControl for value
    });
    this.filtersForm.push(filter);
  }

  onRemoveFilter(index: number): void {
    this.filtersForm.removeAt(index);

    this.filtersChange.emit(this.filtersForm.value);
  }

  onRemoveAllFilters(): void {
    this.filtersForm.clear();

    this.filtersChange.emit(this.filtersForm.value);
  }

  onSortToggle(column: TableColumnConfigInterface<T>): void {
    const currentDirection = this.sortBy.key === column.key ? this.sortBy.direction : undefined;

    let direction: TableSortInterface<T>['direction'];
    if (currentDirection === 'asc') {
      direction = 'desc';
    } else if (currentDirection === 'desc') {
      direction = undefined;
    } else {
      direction = 'asc';
    }

    // a new object instead of a mutation: `sortBy` is often bound straight to (frozen) store state
    this.sortBy = { key: column.key, direction };

    this.sortChange.emit(this.sortBy);
  }

  onToggleCollapseGroup(group: GroupedData<T>): void {
    group.open = !group.open;
  }

  onPageSizeChange(event: MenuSelectionChange<number>): void {
    if (event.added.length === 0) return;

    const pageSize = event.added[0].value;
    this.pageSize = pageSize || 25;

    this.page = 1;
    this.pagination = this._getPagination(this.page, this.totalItems);

    this.pageSizeChange.emit(this.pageSize);
    this.paginationChange.emit(this.pagination);
  }

  nextPage(): void {
    if (this.page * this.pageSize >= this.totalItems) return;
    this.page++;

    this.pagination = this._getPagination(this.page, this.totalItems);
    this.paginationChange.emit(this.pagination);
  }

  previousPage(): void {
    if (this.page === 1) return;
    this.page--;

    this.pagination = this._getPagination(this.page, this.totalItems);
    this.paginationChange.emit(this.pagination);
  }

  firstPage(): void {
    if (this.page === 1) return;
    this.page = 1;

    this.pagination = this._getPagination(this.page, this.totalItems);
    this.paginationChange.emit(this.pagination);
  }

  onCheckAll(): void {
    if (!this.selectable) return;

    const rows = this.rows.toArray().filter((row) => !row.disabled);

    if (!this.allVisibleRowsChecked) {
      rows.forEach((row) => row.select(true));
    } else {
      rows.forEach((row) => row.deselect(true));

      if (this.showOnlySelection) {
        if (this.selectableController.selected.length === 0) return this.onToggleShowOnlySelection();

        this._cd.detectChanges(); // update rows array
        if (this.rows.length === 0) return this.previousPage();
      }
    }
  }

  setShowOnlySelection(showOnlySelection: boolean): void {
    if (this.showOnlySelection === showOnlySelection) return;

    this.onToggleShowOnlySelection();
  }

  onToggleShowOnlySelection() {
    this.showOnlySelection = !this.showOnlySelection;
    this.firstPage();

    const event = new TableShowOnlySelectionChange(
      this.showOnlySelection,
      this.selectableController.selected.map((s) => s.value[this.idKey] as number),
      this.visibleColumns.selected
    );
    this.showOnlySelectionChange.emit(event);
  }

  onRefresh(event: MouseEvent) {
    this.refresh.emit();
  }

  onRowClick(event: PointerEvent, row: T): void {
    if (event.target instanceof HTMLAnchorElement) return;

    if (this.readonly) return;

    this.rowClick.emit(row);
  }

  onNoNavigateRouterLinkClick(event: MouseEvent, row: T): void {
    // stopPropagation is required: without it onRowClick's anchor-bail swallows the row click.
    event.preventDefault();
    event.stopPropagation();
    if (this.readonly) return;
    this.rowClick.emit(row);
  }

  getRouterLinkHref(routerLink: string[] | undefined): string {
    return this._location.prepareExternalUrl((routerLink ?? []).join('/'));
  }

  onRowActionClick(action: TableRowActionInterface<T>, row: T): void {
    action.click(row);
  }

  onCellSwitchChange(row: T, column: TableColumnConfigInterface<T>, value: boolean): void {
    this.cellSwitchChange.emit(new TableCellSwitchChange<T>(row, column, value));
  }

  onGroupActionClick(action: TableGroupActionInterface<T>, group: T): void {
    action.click(group, this);
  }

  onBulkClick(action: TableBulkActionInterface<T>): void {
    action.click(this.selectableController.selected.map((s) => s.value));
  }

  clearSelection() {
    this.selectableController?.clear();
    this.setShowOnlySelection(false);
  }

  deselectRows(ids: number[]) {
    const selection = this.selectableController.selected.filter((row) => ids.includes(row.value[this.idKey as any]));
    if (selection.length) this.selectableController.deselect(...selection);
    if (this.selectableController.isEmpty()) this.setShowOnlySelection(false);
  }

  /**
   * Puts the table's filters, search term, sorting and page back to a previously captured state, without
   * emitting the corresponding change outputs. Silence is the point: the caller already knows the state it
   * is restoring, so echoing it back would trigger a redundant data fetch.
   *
   * Only the keys present on `state` are applied. Filters for columns that no longer exist are dropped by
   * `_initFilterForm`/`filterUnfilterableColumns`, so a stale state cannot break the table.
   */
  public restoreState(state: Partial<TableStateInterface<T>>): void {
    if (state.visibleColumns) {
      this.setVisibleColumns(state.visibleColumns);
    }

    // Filters go first: rebuilding the form resets `page`, which would undo the page we restore below.
    if (state.filters) {
      this._initFilterForm(state.filters, false);
    }

    if (state.sortBy) {
      this.sortBy = { ...state.sortBy };
    }

    if (state.searchTerm !== undefined) {
      this.searchTerm = state.searchTerm;
      this.searchTermControl.setValue(state.searchTerm, { emitEvent: false });
    }

    if (state.pageSize) {
      this.pageSize = state.pageSize;
    }

    if (state.page) {
      this.page = state.page;
    }

    // Not `_getPagination`: that collapses `from` to 0 while `totalItems` is unknown, which reads as
    // "page 1" to whatever fetches the data — a restored page N would silently request page 1 and leave
    // the table claiming to show page N. The total is echoed as-is; the response corrects it.
    const pageSize = this.pageSize ?? 10;
    const from = (this.page - 1) * pageSize;
    this.pagination = {
      from: from + 1,
      to: this.totalItems ? Math.min(from + pageSize, this.totalItems) : from + pageSize,
      total: this.totalItems,
    };
    this._cd.markForCheck();
  }

  public applyFilter(key: keyof T, value: unknown): void {
    const otherFilters = ((this.filtersForm?.value ?? []) as TableFilterInterface<T>[]).filter(
      (filter) => filter.key !== key
    );

    this._initFilterForm([...otherFilters, { key, value }]);
  }

  /**
   * Shows exactly the columns named in `keys`, plus any column the config marks `alwaysVisible`. Keys with
   * no matching column are ignored, so a stale set cannot empty the table. Emits nothing — see
   * {@link restoreState}.
   */
  public setVisibleColumns(keys: string[]): void {
    const wanted = new Set(keys);

    // `visible` is what the column picker reads, and what `_setVisibleColumns` rebuilds the selection from
    // on the next `columns` input, so the flag has to agree with the selection rather than trail behind it.
    this.columns.forEach((column) => (column.visible = wanted.has(column.key as string)));

    // Rebuilt rather than diffed, so the columns render in config order — see `onVisibleColumnsChange`.
    this._initVisibleColumnsSelection();
    this._cd.markForCheck();
  }

  getRowActions(row: T): TableRowActionInterface<T>[] {
    return this.rowActions?.filter((action) => !action.condition || action.condition(row));
  }

  isRowActionDisabled(action: TableRowActionInterface<T>, row: T): boolean {
    return typeof action.disabled === 'function' ? action.disabled(row) : !!action.disabled;
  }

  isRowDisabled(row: T): boolean {
    return !!this.isRowSelectable && !this.isRowSelectable(row);
  }

  getGroupActions(group: T): TableGroupActionInterface<T>[] {
    return this.groupActions?.filter((action) => !action.condition || action.condition(group));
  }

  getBulkActions(): TableBulkActionInterface<T>[] {
    return this.bulkActions?.filter(
      (action) => !action.condition || action.condition(this.selectableController.selected)
    );
  }

  filterSelect(formGroup: FormGroup, index: number) {
    const { key } = formGroup.value;
    if (!key) return;

    const control = this.filtersForm.controls[index];
    const targetUpdateOn = this._getFilterUpdateOn(key);
    if (control.get('value').updateOn === targetUpdateOn) return;

    const newControl = this._formBuilder.group({
      key: this._formBuilder.control(key, { updateOn: 'change' }),
      // Only columns of a different kind land here, so whatever was filled in for the previous one no
      // longer applies to this one.
      value: this._formBuilder.control(null, { updateOn: targetUpdateOn }),
    });

    this.filtersForm.setControl(index, newControl, { emitEvent: false });
  }

  //#endregion

  //#region Private Methods

  /**
   * Collects the toolbar controls the capabilities on this table contribute. Read through the injector
   * rather than injected as a field: the directives that provide them live on this same element and are
   * constructed after the component is, so they only exist by the time this hook runs.
   *
   * `self` keeps it to this table's own capabilities — an action provided further up the tree would
   * otherwise show up on every table below it.
   */
  private _initToolbarActions(): void {
    const actions = this._injector.get(TABLE_TOOLBAR_ACTION, [], { self: true }) ?? [];

    // `sort` is stable, so actions that share an order keep the order they were provided in.
    this.toolbarActions = [...actions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private _initVisibleColumnsSelection(): void {
    const visibleColumns = this.columns.filter((column) => !!column.visible || !!column.alwaysVisible);
    this.visibleColumns = new SelectionModel<TableColumnConfigInterface<T>>(true, visibleColumns, false);
  }

  private _setVisibleColumns(): void {
    this.visibleColumns.setSelection(...this.columns.filter((column) => !!column.visible || !!column.alwaysVisible));
  }

  private _setColumnConfigDict(): void {
    this.columnConfigDict = this.columns.reduce(
      (acc, column) => {
        acc[column.key as string] = column;
        return acc;
      },
      {} as Record<string, TableColumnConfigInterface<T>>
    );
  }

  /**
   * The id a filter option and a filter value have to agree on to count as the same option.
   *
   * A single-select filter option carries `[id]` as its value — see the template — while the select hands
   * this comparison one element of the control's value at a time, so the two sides arrive as `[id]` and
   * `id`. Unwrapping the single-element array lets them meet on the id; without it an option value is only
   * ever compared by array identity, and a value written into the control from outside — a restored filter
   * state — can never find its option and leaves the dropdown blank.
   */
  private _filterOptionId(option: any): any {
    const value = Array.isArray(option) && option.length === 1 ? option[0] : option;

    return value?.value?.[this.idKey] || value?.[this.idKey] || value;
  }

  private _setFilterableColumns(): void {
    this.filterableColumns = this.columns.filter(
      (column) => column.filterable === undefined || column.filterable === true
    );
  }

  private _setSelectableColumns(): void {
    this.selectableColumns = this.columns.filter((column) => !column.hidden && !column.alwaysVisible);
  }

  private _initFilterForm(initialFilters: TableFilterInterface<T>[] = [], emitEvent = true): void {
    const isNewForm = !this.filtersForm;
    if (isNewForm) {
      this.filtersForm = this._formBuilder.array([]);
    } else {
      this.filtersForm.clear({ emitEvent: false });
    }

    this._coerceUnsetBooleanFilters(initialFilters);

    initialFilters.forEach((_filter) => {
      // A filter can outlive its column — a restored state, a config change, a permission change. Such a
      // filter has no config to read, falls back to the default, and is dropped by
      // `filterUnfilterableColumns` below.
      const updateOn = this._getFilterUpdateOn(_filter.key as string);

      this.filtersForm.push(
        this._formBuilder.group({
          key: this._formBuilder.control(_filter.key, { updateOn: 'change' }),
          value: this._formBuilder.control(_filter.value, { updateOn }),
        }),
        { emitEvent: false }
      );
    });

    this.filterUnfilterableColumns();

    this.filtersForm.markAsPristine();
    this.filtersForm.updateValueAndValidity({ emitEvent });

    if (isNewForm) this._watchFilterChanges();

    // A silent rebuild — a restore — still has to travel through the stream, so that what it left in the
    // form becomes what the next change is compared against. Skipped, the next user edit that lands back
    // on the restored value reads as no change at all: nothing is announced and the page never resets.
    // Pushed after the subscription exists on purpose — the subject does not replay.
    if (!emitEvent) this._silentFilterWrite$.next(this.filtersForm.value);
  }

  /**
   * Announces what the user filters on, and resets to the first page while doing so.
   *
   * Both the form's own edits and the silent rebuilds of {@link restoreState} run through here. The tag says
   * which is which: a silent write still has to pass `distinctUntilChanged`, so that the operator's memory of
   * "what was last seen" moves on to the restored value, but it must not be announced — whoever restored it
   * already knows — and must not reset the page it just restored.
   */
  private _watchFilterChanges(): void {
    merge(
      this.filtersForm.valueChanges.pipe(map((filters: TableFilterInterface<T>[]) => ({ filters, announce: true }))),
      this._silentFilterWrite$.pipe(map((filters) => ({ filters, announce: false })))
    )
      .pipe(
        takeUntil(this._destroyed$),
        startWith({ filters: this.filtersForm.value as TableFilterInterface<T>[], announce: true }),
        // Before the comparison rather than inside the subscriber: a restored value is compared against a
        // later edited one, so both have to have been through the same normalization first.
        map(({ filters, announce }) => ({ filters: this._coerceUnsetBooleanFilters(filters), announce })),
        distinctUntilChanged((previous, next) => this._filtersEqual(previous.filters, next.filters))
      )
      .subscribe(({ filters, announce }) => {
        if (!announce) return;

        this.page = 1;
        this.pagination = this._getPagination(this.page, this.totalItems);

        this.filtersChange.emit(filters);
        this.paginationChange.emit(this.pagination);
      });
  }

  private _getFilterUpdateOn(key: string): 'change' | 'blur' {
    const column = this.columnConfigDict[key];

    // A switch never blurs, so a boolean column can't honour filterOnBlur without losing its value.
    if (column?.type === 'boolean') return 'change';
    if (column?.filterOnBlur !== undefined) return column.filterOnBlur ? 'blur' : 'change';

    return 'blur';
  }

  /** A boolean filter is off rather than absent, so an unset one is worth `false`. */
  private _coerceUnsetBooleanFilters(filters: TableFilterInterface<T>[]): TableFilterInterface<T>[] {
    filters.forEach((_filter) => {
      if (this.columnConfigDict[_filter.key as string]?.type === 'boolean' && _filter.value == null) {
        _filter.value = false;
      }
    });

    return filters;
  }

  private _filtersEqual(previous: TableFilterInterface<T>[], next: TableFilterInterface<T>[]): boolean {
    const left = this._normalizeFilters(previous);
    const right = this._normalizeFilters(next);

    return left.length === right.length && left.every((f, i) => ObjectFunctions.deepEquals(f, right[i]));
  }

  /** The rows that count as actual filters — a row whose column is still unset is not one yet. */
  private _normalizeFilters(filters: TableFilterInterface<T>[]): TableFilterInterface<T>[] {
    return filters.filter(
      (f) => f.key !== null && (f.value !== null || this.columnConfigDict[f.key as string]?.type === 'boolean')
    );
  }

  private filterUnfilterableColumns(): void {
    this.filtersForm.value.forEach((filter) => {
      if (!this.filterableColumns.some((column) => column.key === filter.key)) {
        this.filtersForm.removeAt(
          this.filtersForm.value.findIndex((f) => f.key === filter.key),
          { emitEvent: false }
        );
      }
    });
  }

  /** Mirrors what the template renders from `dataSource`: a group's `children`, or the rows themselves. */
  private _getVisibleRows(): T[] {
    if (!this._dataSource?.length) return [];

    if (!this.groupBy) return this._dataSource as T[];

    return (this._dataSource as GroupedData<T>[]).flatMap((group) => group.children ?? []);
  }

  private _getPagination(page: number, total: number): TablePaginationInterface {
    const pageSize = this.pageSize ?? 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize;

    const pagination = {
      from: total ? from + 1 : 0,
      to: Math.min(to, total),
      total,
    };

    return pagination;
  }

  private _getNewPageFromPageSizeChange(currentPage: number, previousPageSize: number, newPageSize: number): number {
    const currentFrom = (currentPage - 1) * previousPageSize;
    return Math.floor(currentFrom / newPageSize) + 1;
  }

  private _onSelect(row: RowInterface<T>, isUserInput: boolean): void {
    const wasSelected = this.selectableController.isSelected(row);

    if (wasSelected !== row.selected) {
      if (!this.multiple) {
        this.selectableController.clear();
      }
      if (row.selected) this.selectableController.select(row);
      else this.selectableController.deselect(row);
    }
  }

  private _getCompareWith(): (o1: any, o2: any) => boolean {
    const compareWith =
      this.compareWith ||
      ((o1: RowInterface<T>, o2: RowInterface<T>) => {
        return o1.value[this.idKey] === o2.value[this.idKey];
      });

    return compareWith;
  }

  selectByData(rowData: T[]) {
    this.selectableController.setRawSelection(...rowData);
  }

  deselectByData(rowData: T[]) {
    this.selectableController.deselect(...(rowData as any));
  }

  private _initSelectableController(): void {
    const initialSelection: RowInterface<T>[] = ArrayFunctions.wrapIntoArray(this.initialSelection).map(
      (is) => ({ value: is }) as RowInterface<T>
    );

    this.selectableController = new SelectableListController(
      isTruthyInput(this.multiple),
      initialSelection,
      true,
      this._getCompareWith()
    );

    this.selectableController.changed.pipe(takeUntil(this._destroyed$)).subscribe((event) => {
      const asSelectable = (row: RowInterface<T>) => {
        if (this.selectableController.isSelectable(row)) return row;
        return this.rows.find((r) => this.selectableController.compareWith(r, row));
      };
      event.added.map(asSelectable).forEach((row) => row?.select());
      event.removed.map(asSelectable).forEach((row) => row?.deselect());

      const { selected } = event;
      const selection = {
        selected: this.multiple ? selected.map((s) => s.value) : selected[0]?.value,
        added: event.added.map((s) => s.value),
        removed: event.removed.map((s) => s.value),
      };

      const selectableRows = this.rows.toArray().filter((r) => !r.disabled);
      this.allVisibleRowsChecked = selectableRows.every((r) => r.selected);
      this.someVisibleRowsChecked = selectableRows.some((r) => r.selected) && !this.allVisibleRowsChecked;

      this.selectionChange.emit(new TableSelectionChange(selection.selected, selection.added, selection.removed));
    });
  }

  private setupSearchTerms(shouldDebounce?: boolean): void {
    this.searchTerms$ = this.searchTermOnEnter
      ? this._searchTerms$
      : shouldDebounce
        ? this.searchTermControl.valueChanges.pipe(debounceTime(this.searchTermsDebounceTime))
        : this.searchTermControl.valueChanges;

    this.searchTermControl.setValue(this.searchTerm, { emitEvent: false });

    this.searchTermSubscription.unsubscribe();
    this.searchTermSubscription = new Subscription();

    this.searchTermSubscription.add(
      this.searchTerms$.subscribe((term) => {
        this.searchTerm = term;

        this.page = 1;
        this.pagination = this._getPagination(this.page, this.totalItems);

        this.searchTermChange.emit(term);
        this.paginationChange.emit(this.pagination);
      })
    );
  }

  private _setTrackById(idKey: Extract<keyof T, number>) {
    this.trackById = function (_, item) {
      return item[idKey] as number;
    };
  }
  //#endregion
}
